/**
 * Dependency-free grouped-bar time-series (Splunk-style volume chart).
 *
 * Renders four series (emailSent / emailFailed / syncSuccess / syncFailed) as
 * grouped vertical bars over shared `buckets`, using hand-rolled inline SVG —
 * no external chart library, no CDN. Native <title> gives per-bar hover
 * tooltips; a legend sits above the plot.
 */

export interface TimeSeriesDatum {
  key: string;
  label: string;
  color: string;
  values: number[];
}

interface TimeSeriesProps {
  buckets: string[];
  series: TimeSeriesDatum[];
  /** Formats a bucket ISO string for the x-axis / tooltips. */
  formatBucket?: (iso: string) => string;
  height?: number;
}

const DEFAULT_HEIGHT = 220;

function defaultFormat(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
  });
}

export function TimeSeries({
  buckets,
  series,
  formatBucket = defaultFormat,
  height = DEFAULT_HEIGHT,
}: TimeSeriesProps): JSX.Element {
  const width = 720;
  const padL = 34;
  const padR = 8;
  const padT = 8;
  const padB = 28;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  const n = buckets.length;
  const max = Math.max(
    1,
    ...series.flatMap((s) => s.values),
  );

  // Nice-ish top gridline value.
  const yTicks = 4;
  const groupW = n > 0 ? plotW / n : plotW;
  const groupPad = Math.min(6, groupW * 0.15);
  const innerW = Math.max(0, groupW - groupPad * 2);
  const barW = series.length > 0 ? innerW / series.length : innerW;

  const yFor = (v: number): number => padT + plotH - (v / max) * plotH;

  // Only label a subset of buckets to avoid clutter.
  const labelEvery = Math.ceil(n / 8) || 1;

  return (
    <div className="w-full">
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s) => (
          <span
            key={s.key}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label="Volume over time"
          preserveAspectRatio="none"
          className="min-w-[560px]"
        >
          {/* gridlines + y labels */}
          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const v = (max / yTicks) * i;
            const y = yFor(v);
            return (
              <g key={i}>
                <line
                  x1={padL}
                  x2={width - padR}
                  y1={y}
                  y2={y}
                  stroke="#1e293b"
                  strokeWidth={1}
                />
                <text
                  x={padL - 6}
                  y={y + 3}
                  textAnchor="end"
                  className="fill-slate-500"
                  style={{ fontSize: 9 }}
                >
                  {Math.round(v)}
                </text>
              </g>
            );
          })}

          {/* bars */}
          {buckets.map((b, bi) => {
            const gx = padL + bi * groupW + groupPad;
            return (
              <g key={b + bi}>
                {series.map((s, si) => {
                  const v = s.values[bi] ?? 0;
                  const h = (v / max) * plotH;
                  const x = gx + si * barW;
                  return (
                    <rect
                      key={s.key}
                      x={x}
                      y={padT + plotH - h}
                      width={Math.max(0.5, barW - 0.5)}
                      height={h}
                      fill={s.color}
                      opacity={v === 0 ? 0.25 : 1}
                    >
                      <title>{`${formatBucket(b)} · ${s.label}: ${v}`}</title>
                    </rect>
                  );
                })}
                {bi % labelEvery === 0 && (
                  <text
                    x={gx + innerW / 2}
                    y={height - 10}
                    textAnchor="middle"
                    className="fill-slate-500"
                    style={{ fontSize: 9 }}
                  >
                    {formatBucket(b)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
